import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { DialBroadcastService } from '../dialpad/dial-broadcast.service';
import { AppInsightsService } from '../app-insights.service';

export interface InboundCallData {
  phoneNumber: string;
  conversationId: string;
}

type CrossDomainMessage =
  | { type: 'outboundCall'; phoneNumber: string }
  | { type: 'inboundCall'; data: InboundCallData }
  | { type: 'callStatus'; callStatus: string }
  | { type: 'waitingOnDisposition'; data: boolean }
  | { type: 'setDialpadToInitialState' }
  | { type: 'setCanClose'; data: boolean }
  | { type: 'notifyMessage'; body: string }

@Injectable({
  providedIn: 'root'
})
export class AdmissionUIMessengerService implements OnDestroy {
  private readonly consentConfirmedSource = new BehaviorSubject<boolean | null>(false);
  consentConfirmed = this.consentConfirmedSource.asObservable();
  private monitorOutboundCallDataCallback = (phoneNumber: string) => { };
  private monitorInboundCallDataCallback = (data: InboundCallData) => { };
  private readonly waitingOnDispositionSubject = new BehaviorSubject<boolean>(false);
  waitingOnDisposition$ = this.waitingOnDispositionSubject.asObservable();
  private readonly pageNameSource = new BehaviorSubject<string>('');
  pageName = this.pageNameSource.asObservable();
  private readonly clearPreviousEmailConversationIdSource = new BehaviorSubject<boolean>(false);
  clearPreviousEmailConversationId = this.clearPreviousEmailConversationIdSource.asObservable();
  private readonly setDialpadInitStateSubject = new BehaviorSubject<boolean>(false);
  setDialpadInitState$ = this.setDialpadInitStateSubject.asObservable();
  private readonly canCloseSubject = new BehaviorSubject<boolean>(false);
  canClose$ = this.canCloseSubject.asObservable();
  private readonly clearInteractionStateSubject = new BehaviorSubject<boolean>(false);
  clearInteractionState$ = this.clearInteractionStateSubject.asObservable();
  private readonly setDialpadMessageNotificationSubject = new BehaviorSubject<string>('');
  setDialpadMessageNotificationState$ = this.setDialpadMessageNotificationSubject.asObservable();
  private readonly setLocalTouchSubject = new BehaviorSubject<boolean>(true);
  setLocalTouch$ = this.setLocalTouchSubject.asObservable();
  private readonly setCallStateSubject = new BehaviorSubject<string>('none');
  setCallState$ = this.setCallStateSubject.asObservable();

  constructor(private readonly appInsightsService: AppInsightsService,
    private readonly dialBroadcastService: DialBroadcastService
  ) {
    if (typeof window !== 'undefined') {
      window.addEventListener('message', this.handleCrossDomainMessage.bind(this), false);
    }
    this.dialBroadcastService.startConnection();
    this.dialBroadcastService.messageReceived$.subscribe((message) => {
      if (message?.type === 'ToggleLocalTouchReceived') {
        this.setLocalTouchSubject.next(message.data.localTouch);
      } else if (message?.type === 'WaitingOnDispositionReceived') {
        this.waitingOnDispositionSubject.next(message.data.data);
      } else if (message?.type === 'SetDialpadToInitialStateReceived') {
        this.setDialpadInitStateSubject.next(true);
      } else if (message?.type === 'ChangeCallStateReceived') {
        this.setCallStateSubject.next(message.data.callState);
      }
    });
  }

  setCallState(callState: string) {
    // Send the CallState value to the leadUI window
    this.dialBroadcastService.send('ChangeCallStateSend', { callState: callState });
  }

  sendLocalTouchToValue(localTouch: boolean) {
    // Send the localTouch value to the leadUI window
    this.dialBroadcastService.send('ToggleLocalTouchSend', { localTouch: localTouch });
  }

  monitorOutboundCallData(callback: (phoneNumber: string) => void) {
    this.monitorOutboundCallDataCallback = callback;
  }

  monitorInboundCallData(callback: (data: InboundCallData) => void) {
    this.monitorInboundCallDataCallback = callback;
  }

  setClearPreviousEmailConversationId(clearPreviousEmailConversationId: boolean) {
    this.clearPreviousEmailConversationIdSource.next(clearPreviousEmailConversationId);
  }

  setPage(pageName: string) {
    this.pageNameSource.next(pageName)
  }

  clearLocalStorageState() {
    this.clearInteractionStateSubject.next(true)
  }

  setConsentConfirmed(consentConfirmed: boolean | null) {
    this.consentConfirmedSource.next(consentConfirmed);
  }

  setOutboundCallDataCrossDomain(
    phoneNumber: string,
    targetWindow: Window,
    targetOrigin: string
  ) {
    const message: CrossDomainMessage = { type: 'outboundCall', phoneNumber };
    if (targetWindow) {
      targetWindow.postMessage(message, targetOrigin);
    } else {
      // Send via signalR
      this.dialBroadcastService.send('OutboundCallSend', message);
    }
  }

  setInboundCallDataCrossDomain(
    phoneNumber: string,
    conversationId: string,
    targetWindow: Window,
    targetOrigin: string
  ) {
    const data: InboundCallData = { phoneNumber, conversationId };
    const message: CrossDomainMessage = { type: 'inboundCall', data };
    if (targetWindow) {
      targetWindow.postMessage(message, targetOrigin);
    }
  }

  /**
   * setWaitingOnDispositionCrossDomain: This method is used to communicate from a modal popup opened via window.open
   * @param waiting - true or false
   */
  public setWaitingOnDispositionCrossDomain(waiting: boolean) {
    // Only works if in a modal popup opened via window.open
    const message: CrossDomainMessage = { type: 'waitingOnDisposition', data: waiting };
    if (typeof window !== 'undefined' && window.opener) {
      window.opener.postMessage(message, window.location.origin);
    }
    // Send also via signalR
    if (this.dialBroadcastService) {
      this.dialBroadcastService.send('WaitingOnDispositionSend', message);
    }
  }

  /**
   * monitorWaitingOnDispositionData: returns an observable of boolean that can be subscribed to
   */
  public monitorWaitingOnDispositionData(): Observable<boolean> {
    return this.waitingOnDisposition$;
  }

  /**
   * monitorSetDialpadInitStateData: returns an observable of boolean that can be subscribed to
   * No data needs to be passed.
   */
  public monitorSetDialpadInitStateData(): Observable<boolean> {
    return this.setDialpadInitState$;
  }

  public setSetDialpadInitStateCrossDomain() {
    // Only works if in a modal popup opened via window.open
    const message: CrossDomainMessage = { type: 'setDialpadToInitialState' };
    if (typeof window !== 'undefined' && window.opener) {
      window.opener.postMessage(message, window.location.origin);
    }
    if (this.dialBroadcastService) {
      // Send also via signalR to init dial for other clients
      this.dialBroadcastService.send('SetDialpadToInitialStateSend', message);
    }
  }

  public setCanCloseCrossDomain(canClose: boolean, targetWindow: Window, targetOrigin: string) {
    const message: CrossDomainMessage = { type: 'setCanClose', data: canClose };
    if (targetWindow) {
      targetWindow.postMessage(message, targetOrigin);
    }
  }

  /**
   * SetDialpadMessageNotification: returns an observable of boolean that can be subscribed to
   * No data needs to be passed.
   */
  public monitorSetDialPadMessageNotification(): Observable<string> {
    return this.setDialpadMessageNotificationState$;
  }

  public setSetDialpadMessageNotification(notificationMessage: string) {
    // Only works if in a modal popup opened via window.open
    const message: CrossDomainMessage = { type: 'notifyMessage', body: notificationMessage };
    if (typeof window !== 'undefined' && window.opener) {
      window.opener.postMessage(message, window.location.origin);
    }
    if (this.dialBroadcastService) {
      // Send also via signalR to init dial for other clients
      this.dialBroadcastService.send('SetDialpadMessageNotificationSend', message);
    }
  }

  private handleCrossDomainMessage(event: MessageEvent) {
    const { data } = event;
    if (!data?.type) return;

    switch (data.type) {
      case 'outboundCall':
        this.monitorOutboundCallDataCallback(data.phoneNumber);
        break;
      case 'inboundCall':
        this.monitorInboundCallDataCallback(data.data);
        break;
      case 'waitingOnDisposition':
        this.waitingOnDispositionSubject.next(data.data);
        break;
      case 'setDialpadToInitialState':
        this.setDialpadInitStateSubject.next(true);
        break;
      case 'setCanClose':
        this.canCloseSubject.next(data.data);
        break;
      case 'notifyMessage':
        this.setDialpadMessageNotificationSubject.next(data.body);
        break;
    }
  }

  ngOnDestroy(): void {
    this.waitingOnDispositionSubject.complete();
    this.setDialpadInitStateSubject.complete();
    this.canCloseSubject.complete();
    this.clearInteractionStateSubject.complete();
    this.setLocalTouchSubject.complete();
    this.setCallStateSubject.complete();
  }
}
